import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';

const B = '#2563eb';

// Barèmes micro-entreprise 2026 (cotisations sociales URSSAF + taux du
// versement libératoire de l'impôt si l'option a été prise).
// Stockés comme valeurs par défaut modifiables, PAS comme vérité figée :
// les taux changent chaque année (le BNC régime général est passé de
// 24,6 % en 2025 à 25,6 % en 2026) et l'utilisateur doit pouvoir corriger
// sans attendre une mise à jour de l'app.
export const BAREMES = [
  { id: 'bic_vente', label: 'Vente de marchandises', taux: 12.3, vfl: 1.0, exemple: 'achat-revente, fourniture de repas ou de logement' },
  { id: 'bic_service', label: 'Prestations de services commerciales ou artisanales (BIC)', taux: 21.2, vfl: 1.7, exemple: 'plomberie, électricité, coiffure à domicile, dépannage' },
  { id: 'bnc_cipav', label: 'Profession libérale affiliée CIPAV (BNC)', taux: 23.2, vfl: 2.2, exemple: 'architecte, ostéopathe, ingénieur conseil' },
  { id: 'bnc_ssi', label: 'Profession libérale au régime général (BNC)', taux: 25.6, vfl: 2.2, exemple: 'consultant, formateur, graphiste, DJ, photographe' },
];

// Seuils 2026. Deux familles à ne pas confondre :
//  - la franchise en base de TVA : la franchir oblige à facturer la TVA,
//    et au-delà du seuil MAJORÉ ça s'applique dès le jour du dépassement
//    (pas au 1er janvier suivant) — c'est le piège classique ;
//  - le plafond du régime micro lui-même : revalorisé en 2026
//    (83 600 € en services contre 77 700 € avant, 203 100 € en vente).
const SEUILS = {
  vente:   { tvaBase: 85000, tvaMajore: 93500, micro: 203100 },
  service: { tvaBase: 37500, tvaMajore: 41250, micro: 83600 },
};

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const iso = (d) => d.toISOString().split('T')[0];

export default function UrssafDeclaration({ user, factures, darkMode, onConfigSaved }) {
  const [declarations, setDeclarations] = useState([]);
  const [editing, setEditing] = useState(!user?.urssaf_periodicite);
  const [saving, setSaving] = useState(false);

  // Formulaire de configuration
  const [periodicite, setPeriodicite] = useState(user?.urssaf_periodicite || 'trimestriel');
  const [activite, setActivite] = useState(user?.urssaf_activite || 'bic_service');
  const [tauxPerso, setTauxPerso] = useState(user?.urssaf_taux != null ? String(user.urssaf_taux) : '');
  const [vflActif, setVflActif] = useState(user?.urssaf_vfl_taux != null);

  useEffect(() => {
    if (!user?.id) return;
    supabase.from('urssaf_declarations').select('*').eq('user_id', user.id)
      .then(({ data, error }) => {
        if (error) { console.error('Erreur déclarations URSSAF:', error); return; }
        setDeclarations(data || []);
      });
  }, [user?.id]);

  const bareme = BAREMES.find(b => b.id === (user?.urssaf_activite || activite)) || BAREMES[1];
  const tauxEffectif = user?.urssaf_taux != null ? parseFloat(user.urssaf_taux) : bareme.taux;
  const tauxVfl = user?.urssaf_vfl_taux != null ? parseFloat(user.urssaf_vfl_taux) : 0;

  const sauvegarderConfig = async () => {
    setSaving(true);
    const b = BAREMES.find(x => x.id === activite) || BAREMES[1];
    const taux = tauxPerso.trim() !== '' ? parseFloat(tauxPerso.replace(',', '.')) : b.taux;
    if (isNaN(taux) || taux < 0 || taux > 100) { setSaving(false); return alert('Taux invalide.'); }
    const maj = {
      urssaf_periodicite: periodicite,
      urssaf_activite: activite,
      urssaf_taux: taux,
      urssaf_vfl_taux: vflActif ? b.vfl : null,
    };
    const { error } = await supabase.from('users').update(maj).eq('id', user.id);
    setSaving(false);
    if (error) return alert('Erreur: ' + error.message);
    onConfigSaved?.(maj);
    setEditing(false);
  };

  // ===== CA RÉELLEMENT ENCAISSÉ, PÉRIODE PAR PÉRIODE =====
  // Base de calcul : la date d'encaissement (`date_paiement`), pas la date
  // d'émission. Les factures réglées avant l'ajout de cette colonne n'en
  // ont pas : on retombe sur la date d'émission mais on le signale, pour
  // ne jamais présenter une approximation comme une donnée certaine.
  const periodes = useMemo(() => {
    const periodiciteUser = user?.urssaf_periodicite;
    if (!periodiciteUser) return [];

    const encaissees = (factures || []).filter(f => f.status === 'paye');
    if (encaissees.length === 0) return [];

    const dateDe = (f) => f.date_paiement || f.date || null;
    const toutesDates = encaissees.map(dateDe).filter(Boolean).sort();
    if (toutesDates.length === 0) return [];

    const premiere = new Date(toutesDates[0]);
    const maintenant = new Date();
    const liste = [];

    if (periodiciteUser === 'mensuel') {
      let curseur = new Date(premiere.getFullYear(), premiere.getMonth(), 1);
      while (curseur <= maintenant) {
        const debut = new Date(curseur.getFullYear(), curseur.getMonth(), 1);
        const fin = new Date(curseur.getFullYear(), curseur.getMonth() + 1, 0);
        liste.push({
          cle: `${debut.getFullYear()}-${String(debut.getMonth() + 1).padStart(2, '0')}`,
          label: `${MOIS[debut.getMonth()]} ${debut.getFullYear()}`,
          debut, fin,
          // Déclaration mensuelle : à faire avant la fin du mois suivant.
          limite: new Date(debut.getFullYear(), debut.getMonth() + 2, 0),
        });
        curseur = new Date(curseur.getFullYear(), curseur.getMonth() + 1, 1);
      }
    } else {
      let annee = premiere.getFullYear();
      let trim = Math.floor(premiere.getMonth() / 3);
      while (new Date(annee, trim * 3, 1) <= maintenant) {
        const debut = new Date(annee, trim * 3, 1);
        const fin = new Date(annee, trim * 3 + 3, 0);
        liste.push({
          cle: `${annee}-T${trim + 1}`,
          label: `T${trim + 1} ${annee} (${MOIS[trim * 3]} → ${MOIS[trim * 3 + 2]})`,
          debut, fin,
          // Échéances trimestrielles : 30 avril, 31 juillet, 31 octobre,
          // 31 janvier N+1 — soit la fin du mois suivant le trimestre.
          limite: new Date(annee, trim * 3 + 4, 0),
        });
        trim += 1;
        if (trim > 3) { trim = 0; annee += 1; }
      }
    }

    return liste.reverse().map(p => {
      const debutStr = iso(p.debut), finStr = iso(p.fin);
      const dedans = encaissees.filter(f => {
        const d = dateDe(f);
        return d && d >= debutStr && d <= finStr;
      });
      const ca = dedans.reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
      const aConfirmer = dedans.filter(f => !f.date_paiement && f.type !== 'avoir').length;
      // Acomptes encaissés sur des factures PAS encore soldées : ils
      // comptent dans le CA réel mais l'app ne sait pas à quelle date ils
      // sont tombés (pas d'historique de paiements), donc ils ne sont pas
      // rattachés à une période. Signalé plutôt que silencieusement oublié.
      const acomptesOrphelins = (factures || []).filter(f =>
        f.status !== 'paye' && parseFloat(f.acompte) > 0
      ).reduce((s, f) => s + (parseFloat(f.acompte) || 0), 0);

      const dejaDeclare = declarations.find(d => d.periode_debut === debutStr && d.periode_fin === finStr);
      const close = p.fin < new Date();

      return { ...p, debutStr, finStr, ca, nb: dedans.length, aConfirmer, acomptesOrphelins, dejaDeclare, close };
    });
  }, [factures, user?.urssaf_periodicite, declarations]);

  const marquerDeclare = async (p) => {
    const cotis = p.ca * (tauxEffectif + tauxVfl) / 100;
    const { data, error } = await supabase.from('urssaf_declarations').insert([{
      user_id: user.id,
      periode_debut: p.debutStr,
      periode_fin: p.finStr,
      ca_encaisse: p.ca,
      cotisations: cotis,
    }]).select().single();
    if (error) return alert('Erreur: ' + error.message);
    setDeclarations([...declarations, data]);
  };

  const annulerDeclare = async (p) => {
    const { error } = await supabase.from('urssaf_declarations').delete().eq('id', p.dejaDeclare.id);
    if (error) return alert('Erreur: ' + error.message);
    setDeclarations(declarations.filter(d => d.id !== p.dejaDeclare.id));
  };

  // ===== SEUILS : CA ENCAISSÉ DEPUIS LE 1ER JANVIER =====
  // Les seuils s'apprécient sur l'année civile et sur l'encaissé, comme les
  // cotisations — on réutilise donc exactement la même base de calcul.
  // Déclaré avant tout `return` conditionnel : un hook doit être appelé dans
  // le même ordre à chaque rendu, y compris sur l'écran de configuration.
  const anneeEnCours = new Date().getFullYear();
  const caAnnee = useMemo(() => {
    const debutAnnee = `${anneeEnCours}-01-01`;
    return (factures || [])
      .filter(f => f.status === 'paye' && ((f.date_paiement || f.date) || '') >= debutAnnee)
      .reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
  }, [factures, anneeEnCours]);

  const seuils = SEUILS[(user?.urssaf_activite || activite) === 'bic_vente' ? 'vente' : 'service'];

  const carte = {
    padding: '20px', borderRadius: '18px',
    background: darkMode ? '#1c1c1e' : '#fff',
    border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0',
    boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)',
  };

  // ===== ÉCRAN DE CONFIGURATION =====
  if (editing) {
    const b = BAREMES.find(x => x.id === activite) || BAREMES[1];
    return (
      <div>
        <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px', letterSpacing: '-0.02em' }}>🧾 Déclaration URSSAF</h2>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>Deux réglages à faire une seule fois, puis PlanifPro calcule tout seul ce que vous avez encaissé par période.</p>

        <div style={{ ...carte, marginBottom: '16px' }}>
          <p style={{ fontSize: '15px', fontWeight: 700, marginBottom: '4px' }}>1. À quelle fréquence déclarez-vous ?</p>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '14px' }}>C'est le choix que vous avez fait à l'inscription sur autoentrepreneur.urssaf.fr.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            {[
              { id: 'mensuel', titre: 'Tous les mois', sous: 'Déclaration avant la fin du mois suivant' },
              { id: 'trimestriel', titre: 'Tous les 3 mois', sous: '30 avril · 31 juillet · 31 octobre · 31 janvier' },
            ].map(o => (
              <button key={o.id} onClick={() => setPeriodicite(o.id)}
                style={{ textAlign: 'left', padding: '14px 16px', borderRadius: '14px', cursor: 'pointer',
                  border: periodicite === o.id ? `2px solid ${B}` : (darkMode ? '1px solid #3a3a3c' : '1px solid #e5e7eb'),
                  background: periodicite === o.id ? (darkMode ? '#1e293b' : '#eff6ff') : 'transparent', color: 'inherit' }}>
                <p style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 2px' }}>{o.titre}</p>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>{o.sous}</p>
              </button>
            ))}
          </div>
        </div>

        <div style={{ ...carte, marginBottom: '16px' }}>
          <p style={{ fontSize: '15px', fontWeight: 700, marginBottom: '4px' }}>2. Quel type d'activité ?</p>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '14px' }}>Ça détermine votre taux de cotisations. En cas de doute, le taux exact est écrit sur votre dernière déclaration URSSAF — vous pourrez le corriger juste en dessous.</p>
          <div style={{ display: 'grid', gap: '8px' }}>
            {BAREMES.map(x => (
              <button key={x.id} onClick={() => { setActivite(x.id); setTauxPerso(''); }}
                style={{ textAlign: 'left', padding: '12px 14px', borderRadius: '12px', cursor: 'pointer',
                  border: activite === x.id ? `2px solid ${B}` : (darkMode ? '1px solid #3a3a3c' : '1px solid #e5e7eb'),
                  background: activite === x.id ? (darkMode ? '#1e293b' : '#eff6ff') : 'transparent', color: 'inherit' }}>
                <p style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 2px' }}>{x.label} — <span style={{ color: B }}>{x.taux} %</span></p>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Ex. : {x.exemple}</p>
              </button>
            ))}
          </div>

          <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Taux personnalisé (optionnel)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="number" step="0.1" min="0" max="100" value={tauxPerso} onChange={e => setTauxPerso(e.target.value)}
                placeholder={String(b.taux)}
                style={{ width: '110px', padding: '9px 12px', borderRadius: '10px', border: darkMode ? '1px solid #3a3a3c' : '1px solid #d2d2d7', background: darkMode ? '#2c2c2e' : '#fff', color: 'inherit', fontSize: '14px' }} />
              <span style={{ fontSize: '14px', color: '#6b7280' }}>% — laissez vide pour utiliser {b.taux} %</span>
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '16px', cursor: 'pointer' }}>
            <input type="checkbox" checked={vflActif} onChange={e => setVflActif(e.target.checked)} style={{ marginTop: '3px', width: '16px', height: '16px', cursor: 'pointer' }} />
            <span>
              <span style={{ fontSize: '14px', fontWeight: 600 }}>J'ai opté pour le versement libératoire de l'impôt</span>
              <span style={{ display: 'block', fontSize: '12px', color: '#6b7280' }}>Option prise auprès de l'URSSAF : vous payez l'impôt sur le revenu en même temps que vos cotisations (+{b.vfl} % pour cette activité). Si vous ne savez pas, c'est que vous ne l'avez probablement pas.</span>
            </span>
          </label>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={sauvegarderConfig} disabled={saving}
            style={{ padding: '12px 26px', background: B, color: '#fff', border: 'none', borderRadius: '980px', cursor: saving ? 'wait' : 'pointer', fontWeight: 700, fontSize: '14px' }}>
            {saving ? 'Enregistrement...' : '✅ Enregistrer'}
          </button>
          {user?.urssaf_periodicite && (
            <button onClick={() => setEditing(false)}
              style={{ padding: '12px 22px', background: darkMode ? '#2c2c2e' : '#f3f4f6', color: darkMode ? '#fff' : '#374151', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>
              Annuler
            </button>
          )}
        </div>
      </div>
    );
  }

  const Jauge = ({ titre, valeur, seuil, note, alerteDes = 0.8 }) => {
    const ratio = seuil > 0 ? valeur / seuil : 0;
    const depasse = ratio >= 1;
    const proche = !depasse && ratio >= alerteDes;
    const couleur = depasse ? '#dc2626' : proche ? '#f59e0b' : '#10b981';
    return (
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '6px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', fontWeight: 700 }}>{titre}</span>
          <span style={{ fontSize: '13px', color: couleur, fontWeight: 700 }}>
            {valeur.toFixed(0)} € / {seuil.toLocaleString('fr-FR')} € ({Math.round(ratio * 100)} %)
          </span>
        </div>
        <div style={{ height: '8px', borderRadius: '999px', background: darkMode ? '#2c2c2e' : '#eef2f7', overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(ratio * 100, 100)}%`, height: '100%', background: couleur, transition: 'width .3s' }} />
        </div>
        {(depasse || proche) && (
          <p style={{ fontSize: '12px', color: couleur, margin: '6px 0 0', fontWeight: 600, lineHeight: 1.5 }}>{note}</p>
        )}
      </div>
    );
  };

  // ===== ÉCRAN PRINCIPAL =====
  const tauxTotal = tauxEffectif + tauxVfl;
  const aConfirmerGlobal = periodes.reduce((s, p) => s + p.aConfirmer, 0);
  const acomptesOrphelins = periodes.length ? periodes[0].acomptesOrphelins : 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <h2 style={{ fontSize: '32px', fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>🧾 Déclaration URSSAF</h2>
        <button onClick={() => setEditing(true)}
          style={{ padding: '9px 18px', background: darkMode ? '#2c2c2e' : '#f3f4f6', color: darkMode ? '#fff' : '#374151', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
          ⚙️ Modifier mes réglages
        </button>
      </div>
      <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
        Déclaration <strong>{user.urssaf_periodicite === 'mensuel' ? 'mensuelle' : 'trimestrielle'}</strong> · cotisations {tauxEffectif} %{tauxVfl > 0 ? ` + ${tauxVfl} % d'impôt libératoire` : ''} · montants basés sur ce que vous avez <strong>réellement encaissé</strong>.
      </p>

      <div style={{ ...carte, marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <p style={{ fontSize: '15px', fontWeight: 700, margin: 0 }}>📊 Où vous en êtes pour {anneeEnCours}</p>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>{caAnnee.toFixed(2)} € encaissés depuis le 1er janvier</p>
        </div>

        <Jauge
          titre="Franchise de TVA"
          valeur={caAnnee}
          seuil={seuils.tvaBase}
          note={caAnnee >= seuils.tvaMajore
            ? `Seuil majoré de ${seuils.tvaMajore.toLocaleString('fr-FR')} € dépassé : la TVA s'applique dès le jour du dépassement, pas au 1er janvier. Vos factures doivent la faire apparaître.`
            : caAnnee >= seuils.tvaBase
              ? `Seuil de base dépassé. Tant que vous restez sous ${seuils.tvaMajore.toLocaleString('fr-FR')} €, la franchise court jusqu'à la fin de l'année — au-delà, TVA immédiate.`
              : `Vous approchez du seuil : au-delà, vous devrez facturer la TVA et retirer la mention « TVA non applicable, art. 293 B du CGI ».`}
        />

        <Jauge
          titre="Plafond du régime micro-entreprise"
          valeur={caAnnee}
          seuil={seuils.micro}
          note={caAnnee >= seuils.micro
            ? `Plafond dépassé. Un dépassement sur une seule année ne fait pas sortir du régime ; deux années consécutives font basculer au réel au 1er janvier suivant.`
            : `Vous approchez du plafond du régime micro, à surveiller d'ici la fin de l'année.`}
        />

        <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0', lineHeight: 1.6 }}>
          Seuils 2026. Les deux sont indépendants : on peut devoir facturer la TVA tout en restant largement sous le plafond micro.
        </p>
      </div>

      {aConfirmerGlobal > 0 && (
        <div style={{ ...carte, marginBottom: '16px', borderLeft: '4px solid #f59e0b', background: darkMode ? '#1c1c1e' : '#fffbeb' }}>
          <p style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 4px', color: '#92400e' }}>⚠️ {aConfirmerGlobal} facture{aConfirmerGlobal > 1 ? 's' : ''} sans date d'encaissement confirmée</p>
          <p style={{ fontSize: '13px', color: darkMode ? '#d1d5db' : '#92400e', margin: 0, lineHeight: 1.6 }}>
            Ces factures ont été réglées avant que PlanifPro n'enregistre la date exacte. Elles sont rattachées à leur <strong>date d'émission</strong> en attendant — ce qui peut les placer dans la mauvaise période. Corrigez-les dans l'onglet Factures (la date est modifiable d'un clic sous chaque facture payée).
          </p>
        </div>
      )}

      {acomptesOrphelins > 0 && (
        <div style={{ ...carte, marginBottom: '16px', borderLeft: '4px solid #7c3aed' }}>
          <p style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 4px' }}>💶 {acomptesOrphelins.toFixed(2)} € d'acomptes non comptés</p>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: 0, lineHeight: 1.6 }}>
            Des acomptes ont été encaissés sur des factures pas encore soldées. Ils font partie de votre CA encaissé, mais PlanifPro ne sait pas à quelle date ils sont tombés — <strong>pensez à les ajouter manuellement</strong> sur votre déclaration.
          </p>
        </div>
      )}

      {periodes.length === 0 ? (
        <div style={{ ...carte, textAlign: 'center', padding: '44px 24px' }}>
          <p style={{ fontSize: '15px', color: '#6b7280', margin: 0 }}>Aucune facture encaissée pour l'instant. Dès que vous marquerez une facture comme payée, la période correspondante apparaîtra ici.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {periodes.map(p => {
            const cotis = p.ca * tauxEffectif / 100;
            const impot = p.ca * tauxVfl / 100;
            const enCours = !p.close;
            const enRetard = p.close && !p.dejaDeclare && p.limite < new Date();
            return (
              <div key={p.cle} style={{ ...carte, borderLeft: `4px solid ${p.dejaDeclare ? '#10b981' : enRetard ? '#ef4444' : enCours ? '#9ca3af' : '#f59e0b'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: '200px' }}>
                    <p style={{ fontSize: '17px', fontWeight: 800, margin: '0 0 2px', textTransform: 'capitalize' }}>{p.label}</p>
                    <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                      {p.nb} facture{p.nb > 1 ? 's' : ''} encaissée{p.nb > 1 ? 's' : ''}
                      {enCours
                        ? ' · période en cours'
                        : ` · à déclarer avant le ${p.limite.toLocaleDateString('fr-FR')}`}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px' }}>CA à déclarer</p>
                    <p style={{ fontSize: '26px', fontWeight: 800, color: B, margin: 0, letterSpacing: '-0.02em' }}>{p.ca.toFixed(2)} €</p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '14px', paddingTop: '14px', borderTop: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <div>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 2px' }}>Cotisations ({tauxEffectif} %)</p>
                    <p style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>{cotis.toFixed(2)} €</p>
                  </div>
                  {tauxVfl > 0 && (
                    <div>
                      <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 2px' }}>Impôt libératoire ({tauxVfl} %)</p>
                      <p style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>{impot.toFixed(2)} €</p>
                    </div>
                  )}
                  <div>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 2px' }}>Total à payer ({tauxTotal.toFixed(1)} %)</p>
                    <p style={{ fontSize: '16px', fontWeight: 800, color: '#dc2626', margin: 0 }}>{(cotis + impot).toFixed(2)} €</p>
                  </div>
                  <div style={{ flex: 1 }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {p.aConfirmer > 0 && (
                      <span title="Des factures de cette période n'ont pas de date d'encaissement confirmée" style={{ fontSize: '11px', fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '4px 9px', borderRadius: '6px' }}>
                        ⚠️ {p.aConfirmer} date{p.aConfirmer > 1 ? 's' : ''} à confirmer
                      </span>
                    )}
                    {p.dejaDeclare ? (
                      <button onClick={() => annulerDeclare(p)} title={`Déclaré le ${new Date(p.dejaDeclare.declare_at).toLocaleDateString('fr-FR')}`}
                        style={{ padding: '8px 16px', background: '#d1fae5', color: '#065f46', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '13px' }}>
                        ✅ Déclaré
                      </button>
                    ) : enCours ? (
                      <span style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>Période pas encore terminée</span>
                    ) : (
                      <button onClick={() => marquerDeclare(p)}
                        style={{ padding: '8px 16px', background: enRetard ? '#ef4444' : B, color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '13px' }}>
                        {enRetard ? '🔴 En retard — marquer déclaré' : 'Marquer comme déclaré'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ ...carte, marginTop: '20px', background: darkMode ? '#1c1c1e' : '#f8faff' }}>
        <p style={{ fontSize: '13px', color: '#6b7280', margin: 0, lineHeight: 1.7 }}>
          <strong>À savoir.</strong> Ces montants sont une <strong>estimation</strong> pour préparer votre déclaration, pas un document officiel : la déclaration elle-même se fait sur <a href="https://www.autoentrepreneur.urssaf.fr" target="_blank" rel="noreferrer" style={{ color: B, fontWeight: 600 }}>autoentrepreneur.urssaf.fr</a>.
          Le calcul ne tient pas compte de la contribution à la formation professionnelle (~0,1 à 0,3 %), ni des éventuelles exonérations (ACRE), ni du décalage de 90 jours qui s'applique à votre toute première déclaration. Vérifiez toujours le montant sur le site de l'URSSAF avant de valider.
        </p>
      </div>
    </div>
  );
}
